<script setup lang="ts">
  import { ref } from "vue";
  import { Button } from "@/components/ui/button";
  import { Label } from "@/components/ui/label";
  import { Upload, Loader2, X } from "lucide-vue-next";
  import useMyFetch from "@stores/useMyFetch";
  import { useToastsStore } from "@stores/toastsStore";
  import { matchesAccept } from "@utils/fileAccept";

  interface Props {
    label?: string;
    uploadEndpoint?: string;
    accept?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    label: "Image",
    uploadEndpoint: "/file_uploads/",
    accept: "image/*",
  });

  const toasts = useToastsStore();

  const modelValue = defineModel<string>({ default: "" });

  const isUploading = ref(false);
  const fileInput = ref<HTMLInputElement | null>(null);

  const handleFileSelect = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;
    if (!matchesAccept(file, props.accept)) {
      toasts.error("Format non pris en charge", file.name);
      target.value = "";
      return;
    }

    isUploading.value = true;
    try {
      const formData = new FormData();
      formData.append("file[attachment]", file);
      formData.append("file[type]", file.type);

      const { data, error } = await useMyFetch(props.uploadEndpoint).post(formData, null).json();
      if (error.value) throw error.value;

      modelValue.value = data.value.url;
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      isUploading.value = false;
      if (fileInput.value) fileInput.value.value = "";
    }
  };

  const removeImage = () => {
    modelValue.value = "";
  };

  const triggerFileInput = () => {
    fileInput.value?.click();
  };
</script>

<template>
  <div class="grid gap-2">
    <Label class="text-sm">{{ props.label }}</Label>
    <div class="flex items-center gap-4">
      <div
        v-if="modelValue"
        class="relative">
        <img
          :src="modelValue"
          :alt="props.label"
          class="h-20 w-20 object-cover rounded-md border" />
        <button
          type="button"
          class="absolute -top-2 -right-2 bg-status-error text-white rounded-full p-1 hover:bg-status-error"
          @click="removeImage">
          <X class="h-3 w-3" />
        </button>
      </div>
      <div>
        <input
          ref="fileInput"
          type="file"
          :accept="props.accept"
          class="hidden"
          @change="handleFileSelect" />
        <Button
          type="button"
          variant="outline"
          size="sm"
          class="text-sm"
          :disabled="isUploading"
          @click="triggerFileInput">
          <Loader2
            v-if="isUploading"
            class="mr-2 h-4 w-4 animate-spin" />
          <Upload
            v-else
            class="mr-2 h-4 w-4" />
          {{ isUploading ? "Uploading..." : modelValue ? "Change Image" : "Upload Image" }}
        </Button>
      </div>
    </div>
  </div>
</template>
